// CustomParagraphExtension.ts
import { Node } from "@tiptap/core"
import { DOMOutputSpec } from "@tiptap/pm/model"

/**
 * Interface for custom paragraph attributes
 */
interface CustomParagraphAttributes {
  class: {
    default: string | null
  }
}

/**
 * Interface for HTML attributes passed to renderHTML
 */
interface HTMLAttributes {
  [key: string]: unknown
}

/**
 * Interface for renderHTML options
 */
interface RenderHTMLOptions {
  HTMLAttributes: HTMLAttributes
}

/**
 * CustomParagraph node for TipTap editor.
 *
 * This node extends the default paragraph node to allow for custom HTML rendering and attributes.
 */
const CustomParagraph = Node.create<
  Record<string, never>,
  CustomParagraphAttributes
>({
  /**
   * The name of the node.
   */
  name: "paragraph",

  /**
   * The group this node belongs to.
   */
  group: "block",

  /**
   * The content model for this node.
   */
  content: "inline*",

  /**
   * Defines how the node is parsed from HTML.
   * @returns An array of objects representing HTML tags
   */
  parseHTML() {
    return [{ tag: "p" }]
  },

  /**
   * Defines how the node is rendered as HTML.
   * @param options - Options for rendering HTML
   * @param options.HTMLAttributes - HTML attributes for the node
   * @returns An array representing the HTML tag and its attributes
   */
  renderHTML({ HTMLAttributes }: RenderHTMLOptions): DOMOutputSpec {
    return ["p", HTMLAttributes, 0]
  },

  /**
   * Adds custom attributes to the node.
   * @returns An object defining the node's attributes
   */
  addAttributes(): CustomParagraphAttributes {
    return {
      class: {
        default: null,
      },
    }
  },
})

export default CustomParagraph
